import {
    Song,
    TiktokAudioAsset,
    TrndsClientSongTiktokAudioStats,
    getSongs,
    getTiktokAudioAssets,
    getTrndsClientTiktokAudioStats,
} from '@round/api';
import { useCallback, useState } from 'react';
import { showNotification } from '../../../../../../helpers';
import useAbortableEffect from '../../../../../../Hooks/useAbortableEffect';
type UseClientTiktokAudiosParams = Parameters<typeof getTrndsClientTiktokAudioStats>[0];

export const useClientTiktokAudios = ({
    page,
    page_size,
    search,
    clientId,
    min_video_count,
    max_video_count,
    is_original,
    ordering,
}: UseClientTiktokAudiosParams) => {
    const [tiktokAudioStats, setTiktokAudioStats] = useState<TrndsClientSongTiktokAudioStats[]>([]);
    const [statsCount, setStatsCount] = useState(0);
    const [tiktokAudioAssets, setTiktokAudioAssets] = useState<TiktokAudioAsset[]>([]);
    const [linkedSongs, setLinkedSongs] = useState<Song[]>([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const fetchTiktokAudioAssets = useCallback(async (...params: Parameters<typeof getTiktokAudioAssets>) => {
        try {
            const assets = await getTiktokAudioAssets(...params);
            setTiktokAudioAssets(assets);
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                return;
            }
            showNotification('Could not fetch TikTok audio assets', 'error');
            setHasError(true);
        }
    }, []);

    const fetchLinkedSongs = useCallback(async (...params: Parameters<typeof getSongs>) => {
        try {
            const {
                data: { results },
            } = await getSongs(...params);
            setLinkedSongs(results);
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                return;
            }
            showNotification('Could not fetch Linked Songs', 'error');
            setHasError(true);
        }
    }, []);

    const fetchAudioStats = useCallback(
        async (...params: Parameters<typeof getTrndsClientTiktokAudioStats>) => {
            setIsLoading(true);
            setHasError(false);
            try {
                const {
                    data: { results, count },
                } = await getTrndsClientTiktokAudioStats(...params);
                setTiktokAudioStats(results);
                setStatsCount(count);

                const audioIds = results.map((audio) => audio.audio_id);
                if (audioIds.length) {
                    await Promise.allSettled([
                        fetchTiktokAudioAssets(audioIds, params[1]),
                        fetchLinkedSongs({ tiktok_audios: audioIds.join(',') }, params[1]),
                    ]);
                }
                setIsInitialized(true);
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                showNotification('Could not fetch TikTok audio stats', 'error');
                setHasError(true);
            } finally {
                setIsLoading(false);
            }
        },
        [fetchTiktokAudioAssets, fetchLinkedSongs]
    );

    useAbortableEffect(
        (signal) => {
            fetchAudioStats(
                { page, page_size, search, clientId, min_video_count, max_video_count, is_original, ordering },
                { signal }
            );
        },
        [fetchAudioStats, page, page_size, search, clientId, min_video_count, max_video_count, is_original, ordering]
    );
    return { tiktokAudioStats, statsCount, tiktokAudioAssets, linkedSongs, isLoading, hasError, isInitialized };
};
